import { useCallback, useRef, useState } from "react";

export const ADMIN_LIST_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/** Cap for admin select-all / bulk row actions. */
export const ADMIN_BULK_ACTION_LIMIT = 50;

/** Page size for exhaustive admin list fetches that must not silently truncate. */
export const ADMIN_LIST_PAGE_SIZE = 200;

export async function runInChunks<T>(
  items: T[],
  chunkSize: number,
  worker: (item: T) => Promise<unknown>,
): Promise<void> {
  const size = Math.max(1, chunkSize);
  for (let i = 0; i < items.length; i += size) {
    const chunk = items.slice(i, i + size);
    await Promise.all(chunk.map((item) => worker(item)));
  }
}

export async function findAllPages<T>(
  loadPage: (page: number) => Promise<T[]>,
  pageSize: number = ADMIN_LIST_PAGE_SIZE,
): Promise<T[]> {
  const size = Math.max(1, pageSize);
  const all: T[] = [];
  let page = 1;
  for (;;) {
    const batch = await loadPage(page);
    all.push(...batch);
    if (batch.length < size) return all;
    page += 1;
  }
}

export async function countGroupedById(
  repository: {
    groupBy: (options: never) => Promise<
      Array<{ $count: number } & Record<string, unknown>>
    >;
  },
  field: string,
  ids: string[],
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const id of ids) {
    counts[id] = 0;
  }
  if (ids.length === 0) return counts;

  const rows = await repository.groupBy({
    group: [field],
    where: {
      [field]: ids.length === 1 ? ids[0] : { $in: ids },
    },
  } as never);

  for (const row of rows) {
    const id = row[field];
    if (typeof id === "string") {
      counts[id] = row.$count;
    }
  }
  return counts;
}

export function useDisplayedIdCounts(
  loadCounts: (ids: string[]) => Promise<Record<string, number>>,
) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const requestIdRef = useRef(0);

  const onDisplayedDataChange = useCallback(
    async (rows: Array<{ id?: string }>) => {
      const ids = rows.map((row) => row.id).filter((id): id is string => !!id);
      const requestId = ++requestIdRef.current;
      if (ids.length === 0) {
        setCounts({});
        return;
      }
      try {
        const next = await loadCounts(ids);
        if (requestId !== requestIdRef.current) return;
        setCounts(next);
      } catch (error) {
        console.error("Failed to load displayed counts", error);
        if (requestId !== requestIdRef.current) return;
        setCounts({});
      }
    },
    [loadCounts],
  );

  return { counts, onDisplayedDataChange };
}

export function adminParticipantActionErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  const text = Array.isArray(data)
    ? data.join(" ")
    : typeof data === "string"
      ? data
      : error instanceof Error
        ? error.message
        : "";

  if (text.includes("OPPORTUNITY_IS_FULL")) {
    return "אין מספיק מקומות במשימה";
  }
  if (text.includes("DAILY_VOLUNTEER_TARGET_REACHED")) {
    return "היעד היומי הושלם, אי אפשר להוסיף עוד מתנדבים היום";
  }
  if (text.includes("OPPORTUNITY_NOT_AVAILABLE")) {
    return "המשימה סגורה או לא פתוחה להרשמה";
  }
  if (text.includes("not for groups")) {
    return "המשימה לא מיועדת לקבוצות";
  }
  if (text.includes("OPPORTUNITY_NOT_FOR_INDIVIDUAL")) {
    return "המשימה מיועדת לקבוצות בלבד";
  }
  if (text.includes("PARTICIPANT_NOT_PENDING")) {
    return "הבקשה כבר טופלה";
  }
  if (text.includes("Group admin")) {
    return "לקבוצה אין מוביל, אי אפשר לרשום אותה";
  }
  return "לא ניתן להשלים את הפעולה";
}

export const LATEST_COMMUNICATION_RECORDS_INCLUDE = {
  communicationRecords: {
    limit: 1,
    orderBy: { createdAt: "desc" as const },
  },
};
